/**
 * Global install prompt manager.
 *
 * Browsers can fire `beforeinstallprompt` BEFORE React mounts, so we capture
 * the event in module-init scope and expose a small API. Components subscribe
 * for changes and call `triggerInstall()` whenever the user clicks our UI.
 */

type Listener = () => void

const listeners = new Set<Listener>()
let deferredPrompt: BeforeInstallPromptEvent | null = null
let installed = false

function notify() {
  for (const listener of listeners) listener()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    notify()
  })

  window.addEventListener('appinstalled', () => {
    installed = true
    deferredPrompt = null
    notify()
  })
}

export function isInstallStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function isInstalled(): boolean {
  return installed || isInstallStandalone()
}

export function hasNativeInstallPrompt(): boolean {
  return deferredPrompt !== null
}

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIos = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIos && isSafari
}

export function getInstallSupportLevel(): 'native' | 'ios-safari' | 'manual-only' {
  if (hasNativeInstallPrompt()) return 'native'
  if (isIosSafari()) return 'ios-safari'
  return 'manual-only'
}

export async function triggerNativeInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable'
  try {
    await deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    deferredPrompt = null
    notify()
    return choice.outcome
  } catch {
    deferredPrompt = null
    notify()
    return 'unavailable'
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
